// frontend/src/components/forms/LoginForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  FaGoogle, FaPhone, FaEnvelope, FaArrowLeft,
  FaSpinner, FaRedo, FaExclamationTriangle, FaCheckCircle
} from 'react-icons/fa';

// One format for every Nigerian number: +234XXXXXXXXXX.
// Fixes "+23408012345678" (a 0 left after +234), "08012345678",
// "2348012345678" and "8012345678".
const normalizeNgPhone = (raw) => {
  let digits = String(raw || '').replace(/\D/g, '');
  if (digits.startsWith('2340')) digits = '234' + digits.slice(4);
  else if (digits.startsWith('0')) digits = '234' + digits.slice(1);
  else if (digits.length === 10) digits = '234' + digits;
  return digits ? `+${digits}` : '';
};

// Returns an error message for the name box, or '' when the name is fine.
const nameProblem = (raw) => {
  const name = String(raw || '').trim();
  if (!name) return 'Please enter your full name.';
  if (/\d/.test(name)) return 'This looks like a phone number. Please type your name here.';
  const letters = (name.match(/\p{L}/gu) || []).length;
  if (letters < 2) return 'Please enter your full name.';
  return '';
};

function LoginForm() {
  const [searchParams] = useSearchParams();
  
  // Modes: 'menu', 'email', 'phone_input', 'phone_password', 'phone_register'
  const [mode, setMode] = useState('menu');

  // Email State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Phone State
  const [phoneNumber, setPhoneNumber] = useState('+234');
  const [otp, setOtp] = useState('');
  const [pinId, setPinId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [phoneBusy, setPhoneBusy] = useState(false);
  // The verify screen is shared by NEW sign-ups and SMS password resets.
  // Only new sign-ups are asked for their name.
  const [isNewPhoneUser, setIsNewPhoneUser] = useState(false);
  const [phoneFullName, setPhoneFullName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);

  // Google sign-in progress: '' (idle), 'popup' (Google window open),
  // 'server' (Google done, our server is logging them in).
  const [googleStage, setGoogleStage] = useState('');
  const googleBusy = googleStage !== '';

  const {
    login,
    loginWithGoogle,
    checkPhone,
    sendOtp,
    verifyOtpAndRegister,
    loading,
    error,
    setError
  } = useAuth();

  // The Continue / Verify buttons should spin whenever EITHER the shared
  // auth loading flag or our own phone step is in flight.
  const busy = loading || phoneBusy;

  const navigate = useNavigate();

  // Check URL params to auto-open phone mode
  useEffect(() => {
      if (searchParams.get('mode') === 'phone') {
          setMode('phone_input');
      }
  }, [searchParams]);

  // Clear errors when switching modes
  useEffect(() => {
    setError(null);
  }, [mode, setError]);

  // Timer Logic for Resend Button
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // --- HANDLERS ---

  const handleGoogleLogin = async () => {
    if (googleBusy) return;            // ignore double clicks
    setGoogleStage('popup');
    try {
      const result = await loginWithGoogle(() => setGoogleStage('server'));
      if (result.success) {
        navigate('/');
        return;                        // page is leaving - keep the spinner
      }
    } catch (err) {
      setError("Google sign-in failed. Please try again.");
    }
    setGoogleStage('');
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    const result = await login({ email, password });
    if (result.success) navigate('/');
  };

  const handlePhoneSubmit = async (e) => {
      e.preventDefault();
      if (busy) return;

      // 1. Format Phone
      const formattedNumber = normalizeNgPhone(phoneNumber);
      if (formattedNumber.length !== 14) {
          setError("Please enter a valid Nigerian phone number, e.g. 0803 123 4567.");
          return;
      }

      setPhoneNumber(formattedNumber);
      setError(null);
      setPhoneBusy(true);

      try {
          // 2. Check if user exists in Database
          const exists = await checkPhone(formattedNumber);

          if (exists) {
              // Existing User -> Go to Password Screen
              setMode('phone_password');
          } else {
              // New User -> Send OTP & Go to Register Screen
              const result = await sendOtp(formattedNumber);
              if (result.success) {
                  setPinId(result.pin_id);
                  setIsNewPhoneUser(true);
                  setPhoneFullName('');
                  setNameTouched(false);
                  setOtp('');
                  setMode('phone_register');
                  setResendTimer(60);
              } else {
                  setError(result.error
                      || "We couldn't send the SMS to that number. Please check it and try again.");
              }
          }
      } catch (err) {
          setError("Something went wrong reaching the server. Please try again.");
      } finally {
          setPhoneBusy(false);
      }
  };

  const handlePhoneLogin = async (e) => {
      e.preventDefault();
      const cleanPhone = phoneNumber.replace('+', '').replace(' ', '');
      const internalEmail = `${cleanPhone}@phone.user`;

      const result = await login({ email: internalEmail, password });
      if (result.success) navigate('/');
  };

  const handlePhoneRegister = async (e) => {
      e.preventDefault();
      if (busy) return;

      if (!/^\d{6}$/.test(otp)) {
          setError("Please enter the 6-digit code from the SMS.");
          return;
      }

      const cleanName = phoneFullName.trim().replace(/\s+/g, ' ');
      if (isNewPhoneUser) {
          const problem = nameProblem(cleanName);
          if (problem) {
              setNameTouched(true);
              setError(problem);
              return;
          }
      }
      if (newPassword.length < 6) {
          setError("Password must be at least 6 characters.");
          return;
      }

      setError(null);
      setPhoneBusy(true);
      try {
          // Verify OTP AND create account (or reset password) with the new password.
          // The name is only sent for a brand-new account.
          const result = await verifyOtpAndRegister(
              phoneNumber, pinId, otp, newPassword,
              isNewPhoneUser ? cleanName : null
          );
          if (result.success) {
              navigate('/');
          } else {
              setError(result.error || "That code wasn't right. Please check it and try again.");
          }
      } catch (err) {
          setError("Something went wrong. Please try again.");
      } finally {
          setPhoneBusy(false);
      }
  };

  const handleResendOtp = async () => {
      if (resendTimer > 0 || busy) return;
      setError(null);
      setPhoneBusy(true);
      try {
          const result = await sendOtp(phoneNumber);
          if (result.success) {
              setPinId(result.pin_id);
              setResendTimer(60);
          } else {
              setError(result.error || "Couldn't resend the SMS. Please try again.");
          }
      } finally {
          setPhoneBusy(false);
      }
  };

  // --- FORGOT PASSWORD HANDLER (Phone) ---
  const handlePhoneForgotPassword = async () => {
      if (busy) return;
      setError(null);
      setPhoneBusy(true);
      try {
          const result = await sendOtp(phoneNumber);
          if (result.success) {
              // Reuse the register screen to set a NEW password (no name needed)
              setPinId(result.pin_id);
              setIsNewPhoneUser(false);
              setMode('phone_register');
              setResendTimer(60);
          } else {
              setError(result.error || "We couldn't send the SMS to that number.");
          }
      } finally {
          setPhoneBusy(false);
      }
  };

  // Name box warning: shown at once if a number lands in it (typed or
  // filled in by the browser), otherwise after the customer leaves the box.
  const nameError = nameProblem(phoneFullName);
  const showNameProblem = isNewPhoneUser && !!nameError &&
      (nameTouched || /\d/.test(phoneFullName));

  // --- RENDER HELPERS ---

  const renderMenu = () => (
    <div className="space-y-4">
      <button
        onClick={() => setMode('phone_input')}
        disabled={googleBusy}
        className="w-full flex items-center justify-center gap-3 bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700 transition shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <FaPhone /> Continue with Phone Number
      </button>

      <div className="flex items-center justify-center gap-4 my-4">
          <div className="h-px bg-gray-300 flex-grow"></div>
          <span className="text-xs text-gray-500 font-bold uppercase">OR</span>
          <div className="h-px bg-gray-300 flex-grow"></div>
      </div>

      <button
        onClick={() => setMode('email')}
        disabled={googleBusy}
        className="w-full flex items-center justify-center gap-3 bg-black text-white py-3 rounded-lg font-bold hover:bg-gray-800 transition shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <FaEnvelope /> Continue with Email
      </button>

      <button
        onClick={handleGoogleLogin}
        disabled={googleBusy}
        aria-busy={googleBusy}
        className="w-full flex items-center justify-center gap-3 bg-white border border-gray-300 text-gray-700 py-3 rounded-lg font-bold hover:bg-gray-50 transition shadow-sm disabled:cursor-wait disabled:bg-gray-50"
      >
        {googleBusy ? (
          <>
            <FaSpinner className="animate-spin text-red-500" />
            {googleStage === 'popup' ? 'Waiting for Google...' : 'Signing you in...'}
          </>
        ) : (
          <><FaGoogle className="text-red-500" /> Continue with Google</>
        )}
      </button>
      {googleStage === 'popup' && (
        <p className="text-xs text-gray-500 text-center -mt-2">
          Choose your Google account in the window that opened.
          If no window appeared, allow pop-ups for this site.
        </p>
      )}
    </div>
  );

  const renderEmailForm = () => (
    <form onSubmit={handleEmailLogin} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:outline-none"
          placeholder="you@example.com"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:outline-none"
          placeholder="••••••••"
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-primary hover:bg-primary-dark text-white font-bold py-3 px-4 rounded-md transition duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {loading ? <><FaSpinner className="animate-spin" /> Logging In...</> : 'Log In'}
      </button>
    </form>
  );

  const renderPhoneInput = () => (
    <form onSubmit={handlePhoneSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
        <input
          type="tel"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:outline-none text-lg font-bold tracking-wide"
          placeholder="+234 800 000 0000"
        />
        <p className="text-xs text-gray-500 mt-1">Format: +234...</p>
      </div>
      <button
        type="submit"
        disabled={busy}
        className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-md transition duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {busy ? <><FaSpinner className="animate-spin" /> Sending code...</> : 'Continue'}
      </button>
    </form>
  );

  const renderPhonePassword = () => (
    <form onSubmit={handlePhoneLogin} className="space-y-4">
      {/* Tells the browser's password manager that the phone number is the
          username. Without it, the browser guesses and pushes a saved
          phone number into the first text box it finds - the name box. */}
      <input
        type="text"
        name="username"
        autoComplete="username"
        value={phoneNumber}
        readOnly
        hidden
      />
      <div className="text-center mb-4">
        <p className="text-sm text-gray-600">Welcome back!</p>
        <p className="font-bold text-gray-800 text-lg">{phoneNumber}</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Enter Password</label>
        <input
          type="password"
          name="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:outline-none text-lg"
          placeholder="••••••••"
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-primary hover:bg-primary-dark text-white font-bold py-3 px-4 rounded-md transition duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {loading ? <><FaSpinner className="animate-spin" /> Logging In...</> : 'Log In'}
      </button>

      <div className="text-center mt-3">
          <button
            type="button"
            onClick={handlePhoneForgotPassword}
            className="text-sm text-primary hover:underline font-medium"
          >
            Forgot Password? / Reset via SMS
          </button>
      </div>
    </form>
  );

  const renderPhoneRegister = () => (
    <form onSubmit={handlePhoneRegister} className="space-y-4">
      {/* Tells the browser's password manager that the phone number is the
          username. Without it, the browser guesses and pushes a saved
          phone number into the first text box it finds - the name box. */}
      <input
        type="text"
        name="username"
        autoComplete="username"
        value={phoneNumber}
        readOnly
        hidden
      />
      <div className="text-center mb-4 bg-blue-50 p-4 rounded-lg border border-blue-100">
        <p className="text-sm text-gray-600 mb-2">Everything Automotive (<strong>EAuto</strong>) sent a <strong>6-digit code</strong> to:</p>
        <p className="font-bold text-gray-800 text-lg mb-2">{phoneNumber}</p>
        <p className="text-xs text-blue-600">
          {isNewPhoneUser
            ? 'Enter the code, your name and a password to create your account.'
            : 'Enter the code below and set your new password.'}
        </p>
      </div>

      <div>
        <label className="block text-sm font-bold text-gray-700 mb-1">1. Enter Verification Code</label>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:outline-none text-center text-2xl tracking-[0.5em] font-bold"
          placeholder="------"
        />
      </div>

      {isNewPhoneUser && (
        <div>
          <label htmlFor="phoneFullName" className="block text-sm font-bold text-gray-700 mb-1">
            2. Your Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="phoneFullName"
            name="full_name"
            autoComplete="name"
            autoCapitalize="words"
            spellCheck={false}
            value={phoneFullName}
            onChange={(e) => setPhoneFullName(e.target.value)}
            onBlur={() => setNameTouched(true)}
            required
            minLength={2}
            maxLength={80}
            aria-invalid={showNameProblem}
            className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:outline-none text-lg ${showNameProblem ? 'border-red-500 bg-red-50 focus:ring-red-400' : 'border-gray-300 focus:ring-primary'}`}
            placeholder="e.g. Chinedu Okafor"
          />
          {showNameProblem ? (
            <p className="text-xs text-red-600 font-semibold mt-1">{nameError}</p>
          ) : (
            <p className="text-xs text-gray-500 mt-1">So we know who to address on quotes, orders and deliveries.</p>
          )}
        </div>
      )}

      <div>
        <label className="block text-sm font-bold text-gray-700 mb-1">
          {isNewPhoneUser ? '3. Set Password' : '2. Set New Password'}
        </label>
        <input
          type="password"
          name="new-password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          minLength={6}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:outline-none text-lg"
          placeholder="Set a secure password"
        />
        <p className="text-xs text-gray-500 mt-1">You will use this to log in next time.</p>
      </div>

      <button
        type="submit"
        disabled={busy}
        className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-md transition duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {busy
          ? <><FaSpinner className="animate-spin" /> Verifying...</>
          : <><FaCheckCircle /> {isNewPhoneUser ? 'Verify & Create Account' : 'Verify & Set Password'}</>}
      </button>

      <div className="text-center mt-4">
        <button
          type="button"
          onClick={handleResendOtp}
          disabled={resendTimer > 0 || loading}
          className={`text-sm font-medium flex items-center justify-center gap-2 mx-auto ${resendTimer > 0 ? 'text-gray-400 cursor-not-allowed' : 'text-primary hover:underline'}`}
        >
          <FaRedo size={12} />
          {resendTimer > 0 ? `Resend available in ${resendTimer}s` : "Didn't receive it? Resend Code"}
        </button>
      </div>
    </form>
  );

  return (
    <div className="max-w-md mx-auto mt-10 bg-white p-8 rounded-lg shadow-xl">

      {/* Header */}
      <div className="mb-8 text-center relative">
        {mode !== 'menu' && (
          <button
            onClick={() => { setMode('menu'); setError(null); }}
            className="absolute left-0 top-1 text-gray-400 hover:text-gray-600"
          >
            <FaArrowLeft />
          </button>
        )}
        <h2 className="text-2xl font-bold text-gray-800">
          {mode === 'menu' && 'Welcome'}
          {mode === 'email' && 'Email Login'}
          {mode === 'phone_input' && 'Phone Login / Register'}
          {mode === 'phone_password' && 'Enter Password'}
          {mode === 'phone_register' && (isNewPhoneUser ? 'Create Your Account' : 'Reset Password')}
        </h2>
      </div>

      {/* Error Display */}
      {error && (
        <div className={`mb-4 p-3 rounded text-sm text-center flex items-center justify-center gap-2 ${error.includes('attempt') ? 'bg-red-100 text-red-700 font-bold border border-red-300' : 'bg-red-50 text-red-600'}`}>
          {error.includes('attempt') && <FaExclamationTriangle />}
          {error}
        </div>
      )}

      {/* Content Switcher */}
      {mode === 'menu' && renderMenu()}
      {mode === 'email' && renderEmailForm()}
      {mode === 'phone_input' && renderPhoneInput()}
      {mode === 'phone_password' && renderPhonePassword()}
      {mode === 'phone_register' && renderPhoneRegister()}

      {/* Footer Links */}
      {mode === 'menu' && (
        <p className="text-center text-sm text-gray-600 mt-6">
          Don't have an account?{' '}
          <Link to="/register" className="text-primary hover:underline font-medium">
            Register here
          </Link>
        </p>
      )}

      {mode === 'email' && (
        <p className="text-center text-sm text-gray-600 mt-4">
          <Link to="/forgot-password" className="text-primary hover:underline font-medium">
            Forgot Password?
          </Link>
        </p>
      )}
    </div>
  );
}

export default LoginForm;